import { rgba } from 'polished';
import get from 'lodash/get';
import { SEAT_RADIUS, SEAT_SIZE /* STAND_SCALE */ } from '../constants/SIZES';
import { drawTickIcon, drawTimesIcon, renderText } from './canvas';
import { ITicket } from '../types/ticket';

interface seatParams {
  ctx: CanvasRenderingContext2D;
  seat:
    | {
        price: number;
        x: number;
        y: number;
        label?: string;
        count?: number;
        isStand?: boolean;
        sector?: string;
      }
    | ITicket;
  active?: boolean;
  hovered?: boolean;
  isSelected?: boolean;
  selectedPrices?: Set<Number>;
  previewMode?: boolean;
  zoomIsMinimum?: boolean;
  isStadium?: boolean;
}

function createSeat({
  ctx,
  seat,
  active = false,
  hovered = false,
  selectedPrices,
  previewMode,
  isSelected = false,
  zoomIsMinimum,
  isStadium,
}: seatParams) {
  let isNotHighlited = false;

  if (selectedPrices && selectedPrices.size) {
    const index = window.Widget.pricesController.pricesIndexes[seat.price];

    isNotHighlited = !selectedPrices.has(index) && !isSelected;
  }

  if (
    window.Widget.ticketsController.leftoversSectors &&
    zoomIsMinimum &&
    !get(selectedPrices, 'size')
  ) {
    isNotHighlited = Boolean(window.Widget.ticketsController.leftoversSectors[seat.sector]);
  }

  // TODO падает в WidgetPreview. Надо бы поправить эту зависимость.
  const activeColor = window.Widget.pricesController
    ? window.Widget.pricesController.pricesColors[seat.price]
    : '#000';

  let radius = SEAT_RADIUS;

  if (active) {
    const coeff = zoomIsMinimum && isStadium ? 1.3 : 1;
    radius = SEAT_RADIUS * 4 * coeff;
  }

  if (hovered || isSelected) {
    radius = SEAT_RADIUS * 6;
  }

  if (seat.isStand) {
    // const MIN_RADIUS_IS_STAND = window.Widget.dpr === 1 ? 1 : 35;
    const coeff = window.Widget.dpr === 1 ? 2 : 1;
    const { currentZoom } = window.Widget.zoomController;
    const zoom = currentZoom < 1 ? currentZoom : 1;

    // radius *= STAND_SCALE;
    radius /= zoom * coeff;
    // radius = Math.max(radius, MIN_RADIUS_IS_STAND);
  }

  if (isNotHighlited) {
    ctx.fillStyle = '#fff';
    ctx.clearRect(seat.x - radius - 1, seat.y - radius - 1, radius * 2 + 2, radius * 2 + 2);
    ctx.fillRect(seat.x - radius - 2, seat.y - radius - 2, radius * 2 + 4, radius * 2 + 4);
  }

  ctx.fillStyle = active ? activeColor : '#ccc';

  if (previewMode) {
    const { schemeWidth, schemeHeight } = window.Widget.schemeParameters;

    ctx.fillStyle = schemeWidth > 5000 || schemeHeight > 5000 ? '#b9b9b9' : '#000';
  }

  if (hovered) {
    ctx.fillStyle = activeColor;
  }

  if (isSelected) {
    ctx.fillStyle = '#fff';
  }

  if (isNotHighlited) {
    ctx.fillStyle = rgba(activeColor, 0.3);
  }

  if (isSelected && seat.count > 1) {
    ctx.fillStyle = activeColor;
  }

  ctx.beginPath();

  ctx.arc(seat.x, seat.y, radius, 0, 2 * Math.PI, false);
  ctx.closePath();
  ctx.fill();

  if (isSelected && !hovered) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = activeColor;
    ctx.stroke();
    drawTickIcon({
      ctx,
      x: seat.x,
      y: seat.y,
      color: activeColor,
    });
  }

  if (isSelected && seat.count > 1 && !hovered) {
    const coeff = (window.Widget.dpr * 2) / 3;
    const { currentZoom } = window.Widget.zoomController;
    const zoom = currentZoom < 1 ? currentZoom : 1;
    let fontSize = 14 * coeff;
    fontSize /= zoom;

    renderText({
      ctx,
      x: seat.x,
      y: seat.y + 2,
      fontSize,
      text: String(seat.count),
    });
  }

  if (isSelected && hovered) {
    ctx.lineWidth = 2;
    ctx.strokeStyle = activeColor;
    ctx.stroke();
    drawTimesIcon({
      ctx,
      x: seat.x,
      y: seat.y,
      color: activeColor,
    });
  }
}

function filterSeats({ seats, x, y, x2, y2 }) {
  return seats.filter((seat) => seat.y >= y && seat.y <= y2 && seat.x >= x && seat.x <= x2);
}

function filterTickets({ tickets, x, y, x2, y2 }) {
  return tickets.reduce((acc, ticket) => {
    const ticketInViewport = ticket.y >= y && ticket.y <= y2 && ticket.x >= x && ticket.x <= x2;

    if (ticketInViewport) {
      acc.push(ticket);
    }

    return acc;
  }, []);
}

function findSeatNearByCursor({ tickets, point }) {
  let focusedSeat = null;

  tickets.forEach((seat) => {
    const x = seat.x - SEAT_RADIUS * 6;
    const y = seat.y - SEAT_RADIUS * 6;
    const x2 = x + SEAT_SIZE * 6;
    const y2 = y + SEAT_SIZE * 6;

    if (point.x >= x && point.y >= y && point.x <= x2 && point.y <= y2) {
      focusedSeat = seat;
    }
  });

  return focusedSeat;
}

export { createSeat, filterSeats, findSeatNearByCursor, filterTickets };
